import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DateRangePicker } from 'react-date-range';
import { enGB } from 'date-fns/locale';
import Datetime from 'react-datetime';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'react-datetime/css/react-datetime.css';
import { FaSearch, FaCalendar, FaChevronDown, FaChevronUp} from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import './../styles/AdvancedSearch.css';
import { useFilterContext, defaultFilters } from './../context/FilterContext';
import { useUserList } from './../hooks/useUserList';
import { useSite } from './../context/SiteContext';
import { useAuth } from '../context/AuthContext';
import { UserType } from './../constants/userType';
import userService from '../services/UserService';

const AdvancedSearch = ({ onRefreshGrid, onAnalystSearchChange }) => {
  const { filters, setFilters, resetFiltersOnly, setPagination } = useFilterContext();

  // collapsed by default
  const { isCollapsed, setIsCollapsed } = useFilterContext();

  // portal target
  const [actionBarNode, setActionBarNode] = useState(null);

  // filter state
  const [localFilters, setLocalFilters] = useState(filters);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);
  const [localDateRange, setLocalDateRange] = useState(filters.dateRange);
  const [localStartTime, setLocalStartTime] = useState(filters.startTime);
  const [localEndTime, setLocalEndTime] = useState(filters.endTime);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canFetch, setCanFetch] = useState(false);
  const [canFetchAnalysts, setCanFetchAnalysts] = useState(false);
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [analystsError, setAnalystsError] = useState(null);
  const query = localFilters.visorRef || '';
  const analystQuery = localFilters.analystUsername || '';
  const { selectedSiteId } = useSite();
  const { userType } = useAuth();
  const isPoliceUser = userType === UserType.POLICE;
  const { users, loading, error } = useUserList(query, selectedSiteId, {
    enabled: canFetch,
    delay: 300,
  });
  
  const handleSelectUser = user => {
    setLocalFilters(prev => ({
      ...prev,
      visorRef: user.username,
    }))
    setCanFetch(false)  // prevent fetch when setting selected value
  }

  const handleSelectAnalyst = analyst => {
    setLocalFilters(prev => ({
      ...prev,
      analystUsername: analyst,
    }))
    onAnalystSearchChange?.(analyst);
    setCanFetchAnalysts(false)  // prevent fetch when setting selected value
  }

  // Fetch analysts when query changes
  useEffect(() => {
    if (!canFetchAnalysts || analystQuery.length < 2) {
      setAnalysts([]);
      setAnalystsError(null);
      return;
    }

    const fetchAnalysts = async () => {
      setAnalystsLoading(true);
      setAnalystsError(null);
      try {
        const response = await userService.getAnalystsList(analystQuery);
        const data = Array.isArray(response) ? response : response.data || [];
        setAnalysts(data.map(item => item.analystUsername || item.username || item));
      } catch (err) {
        setAnalystsError('Failed to fetch analysts');
        setAnalysts([]);
      } finally {
        setAnalystsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchAnalysts();
    }, 300);

    return () => clearTimeout(timer);
  }, [analystQuery, canFetchAnalysts]);

  useEffect(() => {
    const node = document.querySelector('.action-bar');
    setActionBarNode(node);
  }, []);

  useEffect(() => {
    setLocalFilters(filters);
    setLocalDateRange(filters.dateRange);
    setLocalStartTime(filters.startTime);
    setLocalEndTime(filters.endTime);
  }, [filters]);

  useEffect(() => {
    if (isDatePickerOpen) {
      setLocalDateRange(localFilters.dateRange);
      setLocalStartTime(localFilters.startTime);
      setLocalEndTime(localFilters.endTime);
    }
  }, [isDatePickerOpen, localFilters]);

  useEffect(() => {
    const onClickOutside = e => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target) &&
        isDatePickerOpen
      ) {
        setLocalFilters(prev => ({
          ...prev,
          dateRange: localDateRange,
          startTime: localStartTime,
          endTime: localEndTime,
        }));
        setDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isDatePickerOpen, localDateRange, localStartTime, localEndTime]);

  const handleLocalDateRangeChange = ranges => {
    const { startDate, endDate } = ranges.selection;
    const forcedStart = new Date(startDate); forcedStart.setHours(0,0,0,0);
    const forcedEnd   = new Date(endDate);   forcedEnd.setHours(23,59,59,999);
    setLocalDateRange([ranges.selection]);
    setLocalStartTime(forcedStart);
    setLocalEndTime(forcedEnd);
  };

  const handleLocalTimeChange = (type, value) => {
    const dateValue = value?.toDate ? value.toDate() : value;
    type === 'startTime'
      ? setLocalStartTime(dateValue)
      : setLocalEndTime(dateValue);
  };

  const handleVisorRefChange = e => {
    const { value } = e.target
    setCanFetch(true)
    setLocalFilters(prev => ({
      ...prev,
      visorRef: value,
    }))
  }

  const handleAnalystRefChange = e => {
    const { value } = e.target
    setCanFetchAnalysts(true)
    setLocalFilters(prev => ({
      ...prev,
      analystUsername: value,
    }))
    onAnalystSearchChange?.(value);
  }

  const handleFilterChange = e => {
    const { name, value, type, checked } = e.target
    const numeric = ['positive','quarantine','status','action']
    setLocalFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : numeric.includes(name)
          ? Number(value)
          : value,
    }))
  }

  const handleResetField = key => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: defaultFilters[key],
    }));
    if (key === 'analystUsername') {
      onAnalystSearchChange?.(defaultFilters.analystUsername);
    }
    if (key === 'dateRange') {
      setLocalDateRange(defaultFilters.dateRange);
      setLocalStartTime(defaultFilters.startTime);
      setLocalEndTime(defaultFilters.endTime);
    }
  };

  const applyAllFilters = () => {
    setPagination(p => ({ ...p, currentPage: 1 }));
    setFilters(localFilters);
  };

  const handleResetAll = () => {
    // Reset local state to default values
    setLocalFilters(defaultFilters);
    setLocalDateRange(defaultFilters.dateRange);
    setLocalStartTime(defaultFilters.startTime);
    setLocalEndTime(defaultFilters.endTime);
    setCanFetch(false);
    setCanFetchAnalysts(false);
    onAnalystSearchChange?.(defaultFilters.analystUsername);
    
    // Reset global filters without navigation
    resetFiltersOnly();
  };

  const formatDateTimeRange = () => {
    if (!localStartTime || !localEndTime) return 'Select Date';
    const fmt = d =>
      `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`;
    return `${fmt(localStartTime)} → ${fmt(localEndTime)}`;
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setFilters(prev => ({ ...prev }));
    if (typeof onRefreshGrid === 'function') {
      onRefreshGrid();
    }
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const toggleButton = (
    <button
     className="toggle-icon-button app-btn"
      title="Toggle Advanced Search"
      onClick={() => setIsCollapsed(c => !c)}
      aria-pressed={!isCollapsed}
    >
      {isCollapsed ? <FaChevronDown/> : <FaChevronUp/>}
      <span className="toggle-text">Filters</span>
    </button>
  );

  const refreshButton = (
    <button
      type="button"
      className="toggle-refresh-icon-button app-btn"
      onClick={handleRefresh}
      disabled={isRefreshing}
    >
      {isRefreshing
        ? 'Refreshing...'
        : (
          <>
            <FontAwesomeIcon icon={faRefresh} className="icon-spacing"/> Refresh
          </>
        )}
    </button>
  );

  return (
    <>
      {actionBarNode && createPortal(toggleButton, actionBarNode)}
      {actionBarNode && createPortal(refreshButton, actionBarNode)}

      <div
        className={`advanced-search-content ${
          isCollapsed ? 'collapsed' : 'expanded'
        }`}
      >
        <div className="advanced-search">
          <div className="filter-container">
            <div className="advanced-search-input-group">
              <span className="advanced-search-input-group-text"><FaSearch/></span>
              <input
                type="search"
                name="visorRef"
                placeholder="Search by Device"
                value={localFilters.visorRef}
                onChange={handleVisorRefChange}
                className="advanced-search-form-control app-input"
                autoComplete="off"
              />     

              {canFetch && query.length >= 3 && (
                <ul className="user-dropdown">
                  {loading && <li className="loading">Loading…</li>}
                  {error   && <li className="no-results">No users found</li>}

                  {!loading && !error && users.length === 0 && (
                    <li className="no-results">No users found</li>
                  )}

                  {!loading && !error && users.map(u => (
                    <li
                      key={u.id}
                      onClick={() => handleSelectUser(u)}
                    >
                      {u.username}
                    </li>
                  ))}
                </ul>
              )}
              {localFilters.visorRef !== defaultFilters.visorRef && (
                <span
                  className="advanced-text-input-filter-badge input-option-selected"
                  onClick={() => handleResetField('visorRef')}
                  title="Reset this filter"
                >×</span>
              )}
            </div> 
          </div>
          {(userType === UserType.SUPER || userType === UserType.TEAMLEADER) && (
            <div className="advanced-search-input-group">
              <span className="advanced-search-input-group-text"><FaSearch/></span>
              <input
                type="search"
                name="analystUsername"
                placeholder="Search by Analyst Username"
                value={localFilters.analystUsername}
                onChange={handleAnalystRefChange}
                className="advanced-search-form-control app-input"
                autoComplete="off"
              />

              {canFetchAnalysts && analystQuery.length >= 2 && (
                <ul className="user-dropdown">
                  {analystsLoading && <li className="loading">Loading…</li>}
                  {analystsError   && <li className="no-results">No analysts found</li>}

                  {!analystsLoading && !analystsError && analysts.length === 0 && (
                    <li className="no-results">No analysts found</li>
                  )}

                  {!analystsLoading && !analystsError && analysts.map((analyst, idx) => (
                    <li
                      key={idx}
                      onClick={() => handleSelectAnalyst(analyst)}
                    >
                      {analyst}
                    </li>
                  ))}
                </ul>
              )}
              {localFilters.analystUsername !== defaultFilters.analystUsername && (
                <span
                  className="advanced-text-input-filter-badge input-option-selected"
                  onClick={() => handleResetField('analystUsername')}
                  title="Reset this filter"
                >×</span>
              )}
            </div>
          )} 

          {/* — Date & Time picker — */}
          <div className="filter-container">
            <span className="datetime-input-group-date"><FaCalendar/></span>
            <div className="date-picker-wrapper">
              <button
                className="btn-datetime app-btn"
                type="button"
                onClick={() => setDatePickerOpen(o => !o)}
                title={formatDateTimeRange()}
              >
                {isDatePickerOpen ? 'Close Picker' : formatDateTimeRange()}
              </button>
              {isDatePickerOpen && (
                <div className="date-picker-overlay" ref={datePickerRef}>
                  <DateRangePicker
                    onChange={handleLocalDateRangeChange}
                    ranges={localDateRange}
                    locale={enGB}
                    dateDisplayFormat="dd/MM/yyyy"
                    className="date-picker"
                  />
                  <div className="time-picker">
                    <label>Start Time:</label>
                    <Datetime
                      value={localStartTime}
                      onChange={v => handleLocalTimeChange('startTime', v)}
                      dateFormat={false}
                      timeFormat="HH:mm"
                    />
                    <label>End Time:</label>
                    <Datetime
                      value={localEndTime}
                      onChange={v => handleLocalTimeChange('endTime', v)}
                      dateFormat={false}
                      timeFormat="HH:mm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* — Dropdown filters — */}
          {['positive', !isPoliceUser ? 'quarantine' : null,'status','action'].filter(Boolean).map(key => (
            <div key={key} className="filter-container">
              <select
                name={key}
                value={
                  localFilters[key] !== undefined
                    ? localFilters[key]
                    : defaultFilters[key]
                }
                onChange={handleFilterChange}
                className="img-form-select app-input"
              >
                {key==='positive' && <>
                  <option value="0">Positive</option>
                  <option value="1">Positive: Yes</option>
                  <option value="2">Positive: No</option>
                </>}
                {
                  !isPoliceUser && 
                  key==='quarantine' && <>
                  <option value="0">Quarantine</option>
                  <option value="1">Quarantine: Yes</option>
                  <option value="2">Quarantine: No</option>
                </>}
                {key==='status' && <>
                  <option value="2">Not Reviewed</option>
                  <option value="0">Important</option>
                  <option value="1">Not Important</option>
                  <option value="10">All</option>
                </>}
                {key==='action' && <>
                  <option value="0">Action</option>
                  <option value="1">Action: Yes</option>
                  <option value="2">Action: No</option>
                </>}

              </select>
              {localFilters[key] !== defaultFilters[key] && (
                <span
                  className="advanced-filter-badge option-selected"
                  onClick={() => handleResetField(key)}
                  title="Reset this filter"
                >×</span>
              )}
            </div>
          ))}

          <div className="apply-reset-buttons">
            <button
              type="button"
              className="btn-apply app-btn"
              onClick={applyAllFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="btn-reset app-btn"
              onClick={handleResetAll}
            >
              Reset
            </button>            
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvancedSearch;
